import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useNavigationType } from 'react-router';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import type { DirectoryEntityType } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import { LOAD_MORE_DIRECTORY_ACTION } from '~/routes/actions.load-more-directory';
import { useTracker } from '@archipro-website/tracker';

/**
 * Products per page for the legacy A/B/A products grid — 42 = 3 sections × 14.
 * Both the initial load and each "View More" click load this many.
 */
export const PRODUCTS_PER_PAGE = 42;

/**
 * Page size for the 5.0 uniform browse grid (DirectoryUniformGrid). 36 divides
 * evenly by 2, 3, 4 and 6 columns, so at those counts a page always ends on a
 * full row with an injected module landing on the boundary. The one exception
 * is the Display dropdown's 5-column option (36 % 5 = 1): its last row runs
 * ragged and injections land mid-page — accepted, since tiles reflow into one
 * continuous grid as pages load.
 *
 * It is a page of products AND the grid's cell budget: amplifier (AAA) cells
 * spend cells out of it rather than adding to it, so how many amplified
 * professionals a category has — three, one, or none — never changes the page
 * shape or the `?page=N` offsets. It is exactly one AMPLIFIER_PAGE_CELLS cycle.
 *
 * Only multiples of that cycle are worth setting here. While more products are
 * still to come the grid renders whole cycles only (DirectoryUniformGrid holds
 * the surplus back so the last row is never short), so 48 or 60 would fetch
 * more than 36 and still draw 36 — the extra is paid for and not shown.
 */
export const BROWSE_PRODUCTS_PER_PAGE = 36;

// ============================================================================
// Pagination Helper Functions
// ============================================================================

/**
 * Calculate total products loaded up to and including a given page.
 * Page 1: pageSize, Page 2: 2·pageSize, Page N: pageSize · N.
 */
export const getProductCountForPage = (
    page: number,
    pageSize: number = PRODUCTS_PER_PAGE,
): number => {
    if (page <= 0) return 0;
    return pageSize * page;
};

/**
 * Get section indices for a given page.
 * Every page has 3 sections (42 products = 3 × 14).
 * Page 1: sections [0, 1, 2]
 * Page 2: sections [3, 4, 5]
 * Page N: sections [3*(N-1), 3*(N-1)+1, 3*(N-1)+2]
 */
export const getSectionsForPage = (page: number): number[] => {
    const start = 3 * (page - 1);
    return [start, start + 1, start + 2];
};

/**
 * Get layout type for a section index.
 * Even indices = Layout A (featured on right)
 * Odd indices = Layout B (featured on left)
 */
export const getLayoutForSection = (sectionIndex: number): 'A' | 'B' => {
    return sectionIndex % 2 === 0 ? 'A' : 'B';
};

/**
 * Calculate maximum valid page number for a given total count.
 */
export const getMaxPage = (
    totalCount: number,
    pageSize: number = PRODUCTS_PER_PAGE,
): number => {
    if (totalCount <= 0) return 1;
    return Math.ceil(totalCount / pageSize);
};

interface UseProductViewMoreParams {
    /**
     * Initial products from the server-side loader.
     */
    initialProducts: TileViewModel<TileableModel>[];
    /**
     * Total number of products available in this category.
     */
    totalCount: number;
    /**
     * The category link path (e.g., "/products/kitchens").
     */
    categoryLink: string;
    /**
     * Optional: Active filters to apply when loading more products.
     */
    filters?: Record<string, unknown>;
    /**
     * Optional: the in-listing keyword term. Repeated on every later page so
     * View More stays inside the result set the first page came from.
     */
    search?: string;
    /**
     * Deep-linking: First page that has loaded products (1-indexed).
     * When provided, enables skeleton lazy-loading for pages below this.
     * Default: 1 (no skeletons, all products loaded from page 1)
     */
    firstLoadedPage?: number;
    /** Directory entity (drives the search index server-side). Default products. */
    entityType?: DirectoryEntityType;
    /** Tiles per page / View More step. Default {@link PRODUCTS_PER_PAGE} (42). */
    pageSize?: number;
    /**
     * Active sort from the URL (Sort dropdown), forwarded to the load-more
     * fetches so appended pages keep the same order as the initial grid.
     */
    sortBy?: string;
    /**
     * Absolute offset the initial grid started at. Added to every load-more
     * request so appended pages continue from where the server left off rather
     * than from zero. Non-zero only when the server entered the result set part
     * way in, as the curated products root does. Default 0.
     */
    offsetBase?: number;
    /**
     * What makes two tiles the same tile. Defaults to the model id, which is
     * what every grid whose rows map one-to-one onto tiles wants. The image
     * walls key on the photo instead: one photo can be indexed under several
     * ids, and pages far apart can each carry one of them.
     */
    dedupeKey?: (model: TileableModel) => string | number;
    /**
     * Rewrite `?page=N` (and log DirectoryPageChange) when a load-more fetch
     * starts. Accurate for the button-only legacy grids, but ahead of the
     * viewport under infinite scroll — the revamp browse passes false and
     * DirectoryUniformGrid keeps the param in step with the page actually
     * being viewed instead (useViewportPageSync). Default true.
     */
    updateUrlOnLoad?: boolean;
    /**
     * The content gate is closed on this request: the loader already truncated
     * the tile list, so there is nothing further to fetch. Reports no more
     * results, which also disarms every path into a load-more fetch — the
     * button, the infinite-scroll sentinel and skeleton lazy-loading alike.
     */
    gated?: boolean;
    /**
     * Resource route serving the pages. Defaults to the directory action, which
     * reads the three search-index entity types.
     *
     * The editorial article grids point this at their own route instead: an
     * article listing is not one of those entity types and has no index behind
     * it, so it cannot be reached through `entityType` without inventing a
     * search type for it. Anything set here must answer the same contract —
     * `offset`, `limit`, `filters`, `search`, `sort` in; `{ tiles, hasMore,
     * totalCount }` out — since every other behaviour here is built on it.
     */
    actionPath?: string;
}

interface UseProductViewMoreReturn {
    /**
     * All products currently loaded (skeleton pages + initial + View More).
     * Products are merged in correct order for grid rendering.
     */
    products: TileViewModel<TileableModel>[];
    /**
     * Whether there are more products to load (via View More).
     */
    hasMore: boolean;
    /**
     * Whether a View More fetch is currently in progress.
     */
    isLoading: boolean;
    /**
     * Error message if the last fetch failed, null otherwise.
     */
    error: string | null;
    /**
     * Function to trigger loading more products (View More button).
     */
    loadMore: () => void;
    /**
     * Function to load products for a skeleton page (lazy-loading).
     * Called when a skeleton section enters the viewport.
     */
    loadSkeletonPage: (pageNumber: number) => void;
    /**
     * Set of page numbers currently being loaded (for skeleton pages).
     */
    loadingPages: Set<number>;
    /**
     * Current first loaded page (updates when skeleton pages load).
     * Used by the grid to determine how many skeletons to render.
     */
    currentFirstLoadedPage: number;
    /**
     * Whether products were restored from cache (POP navigation).
     * Used to suppress deep-link scrollIntoView on back navigation.
     */
    isRestoredFromCache: boolean;
    /**
     * The `?page=N` value the next Load More / View More step lands on. Used to
     * render the Load More control as a real crawlable anchor (`<a href>`) so
     * bots can walk the item set without executing JS.
     */
    nextPage: number;
}

// Module-level cache for product state — persists across navigations within the same tab.
// On POP (back/forward) navigation, the exact products the user saw are restored,
// preserving DOM height so scroll position restoration works correctly.
const viewMoreCache = new Map<
    string,
    {
        products: TileViewModel<TileableModel>[];
        serverHasMore: boolean;
    }
>();

interface LoadMoreResponse {
    /**
     * Row the server's read reached, when it differs from the number of tiles
     * returned — a grid that collapses rows into fewer tiles reports it so the
     * next page carries on from the right place rather than from the tile
     * count.
     */
    nextOffset?: number;
    tiles: TileViewModel<TileableModel>[];
    hasMore: boolean;
    totalCount: number;
    /**
     * Set when the page could not be read — the search gateway answered
     * 502, say. Carried in the body of a 200 rather than as an error status:
     * a failed `fetcher.load` goes to the route's error boundary and takes
     * the whole page with it, when all that is lost is one more page of
     * tiles. The grid keeps what it has, and the hook retries.
     */
    error?: string;
}

/**
 * Automatic retries of a View More page that failed, after the first attempt.
 *
 * One, not more, because the request has already been tried twice by the time
 * a failure reaches here: the Search Service call retries gateway-class
 * statuses itself before the route reports the page as failed. A single retry
 * here is the third and fourth upstream attempt, spread over a second and a
 * half, which is what the bursts this exists for need. A page still failing
 * after that is not a burst, and a further round would only add load to a
 * gateway that is already down — so the reader gets the message and the View
 * More control instead.
 */
const MAX_AUTO_RETRIES = 1;

/** Pause before the automatic retry. */
const RETRY_BASE_DELAY_MS = 1000;

/**
 * Random extra pause on top of it. Every reader looking at a wall when the
 * gateway fails schedules a retry in the same instant, so a fixed delay
 * would land them all on it together — the same reason the Search Service
 * jitters its own retry.
 */
const RETRY_JITTER_MS = 500;

/** Copy shown under the grid when a page could not be loaded. */
export const LOAD_MORE_FAILED_MESSAGE =
    'We could not load more results. Please try again.';

/**
 * Hook for managing "View More" product loading functionality.
 *
 * Features:
 * - Client-side pagination using useFetcher (no URL changes)
 * - Appends new products to existing list
 * - Handles race conditions with AbortController
 * - Resets state when category/filters change
 * - Provides loading and error states
 *
 * @example
 * ```tsx
 * const { products, hasMore, isLoading, error, loadMore } = useProductViewMore({
 *   initialProducts: loaderProducts,
 *   totalCount: pagination.totalCount,
 *   categoryLink: '/products/kitchens',
 * });
 * ```
 */
export function useDirectoryViewMore({
    initialProducts,
    totalCount,
    categoryLink,
    filters,
    search,
    firstLoadedPage = 1,
    entityType = 'products',
    pageSize = PRODUCTS_PER_PAGE,
    sortBy,
    offsetBase = 0,
    dedupeKey,
    updateUrlOnLoad = true,
    gated = false,
    actionPath = LOAD_MORE_DIRECTORY_ACTION,
}: UseProductViewMoreParams): UseProductViewMoreReturn {
    const fetcher = useFetcher<LoadMoreResponse>();
    const navigationType = useNavigationType();
    const tracker = useTracker();

    // Page-size-aware offset helpers (the exported product ones are fixed at 42).
    const tileCountForPage = (page: number): number =>
        page <= 0 ? 0 : pageSize * page;
    const pageFromTileCount = (count: number): number =>
        count <= 0 ? 1 : Math.ceil(count / pageSize);

    // On POP navigation, restore cached product state to preserve exact products
    // the user saw before navigating away. This ensures DOM height is preserved
    // so scroll position restoration works correctly.
    const cachedState =
        navigationType === 'POP' ? viewMoreCache.get(categoryLink) : undefined;
    const isRestoredFromCache = !!cachedState;

    // Pool of 3 fetchers for parallel skeleton loading
    const skeletonFetcher0 = useFetcher<LoadMoreResponse>();
    const skeletonFetcher1 = useFetcher<LoadMoreResponse>();
    const skeletonFetcher2 = useFetcher<LoadMoreResponse>();
    // Memoize the array to prevent dependency changes on every render
    const skeletonFetchers = useMemo(
        () => [skeletonFetcher0, skeletonFetcher1, skeletonFetcher2],
        [skeletonFetcher0, skeletonFetcher1, skeletonFetcher2],
    );

    // State for accumulated products (server-loaded + View More loaded)
    // On POP, restore from cache; otherwise use loader's initialProducts
    const [products, setProducts] = useState<TileViewModel<TileableModel>[]>(
        cachedState?.products ?? initialProducts,
    );
    const [error, setError] = useState<string | null>(null);

    // Tracks whether a View More fetch is pending AND products haven't been appended yet.
    // Prevents the gap render where fetcher.state='idle' (skeleton removed) but
    // setProducts() hasn't fired yet (products not appended).
    const [isViewMoreLoading, setIsViewMoreLoading] = useState(false);
    // A failed View More is being retried automatically. Reads as loading to
    // the grid — the skeleton stays and the scroll sentinel holds off — but
    // not to `loadMore`, which the retry goes through.
    const [retryPending, setRetryPending] = useState(false);

    // Track hasMore from server response (more reliable than calculating from totalCount)
    const [serverHasMore, setServerHasMore] = useState(() => {
        if (cachedState?.serverHasMore !== undefined) {
            return cachedState.serverHasMore;
        }
        // Account for deep-link offset: products start at firstLoadedPage, not page 1.
        // Without this, deep-linking to the last page (e.g., ?page=16) would show
        // hasMore=true because 126 < 672, when in reality pages 14-16 are fully loaded.
        // `offsetBase` shifts where the server started, so it counts towards
        // the pool consumed — otherwise a curated block near the end of the
        // pool keeps offering View More with nothing left to fetch.
        const serverEndOffset =
            offsetBase +
            tileCountForPage(firstLoadedPage - 1) +
            initialProducts.length;
        return serverEndOffset < totalCount;
    });

    // Fetch identity counter: incremented each time loadMore() initiates a fetch.
    // This replaces the previous hasFetchedRef two-effect pattern which was fragile
    // under React 18's startTransition (used by Remix's router), where the 'loading'
    // state could be skipped entirely, preventing the ref from being reset.
    const fetchCountRef = useRef(0);
    // Set from the server's `nextOffset` when it sends one; otherwise the tile
    // count is the offset, as it is for every grid that maps rows to tiles
    // one for one.
    const nextOffsetRef = useRef<number | null>(null);
    // Tracks which fetch has been processed. Set to fetchCountRef.current after
    // processing, and also on filter/category resets to invalidate in-flight fetches.
    const processedFetchCountRef = useRef(0);
    // Whether the page in hand has already had its automatic retry.
    // Reset by any page that arrives.
    const failedAttemptsRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Failed skeleton pages waiting out the gateway before they go back on the
    // pool's queue. One per page, so several can be in hand at once.
    const skeletonRetryTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(
        new Set(),
    );
    // `processQueue` closes over the render's fetcher objects and is rebuilt
    // every render, so a timer holding one from a second ago would read a
    // stale pool. Same reason as `loadMoreRef` below.
    const processQueueRef = useRef<() => void>(() => {});
    // The retry timer fires after the `loadMore` it captured has been
    // replaced, so it goes through a ref to the current one.
    const loadMoreRef = useRef<() => void>(() => {});
    const cancelRetry = useCallback(() => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        for (const timer of skeletonRetryTimersRef.current) {
            clearTimeout(timer);
        }
        skeletonRetryTimersRef.current.clear();
        failedAttemptsRef.current = 0;
        setRetryPending(false);
    }, []);
    useEffect(() => cancelRetry, [cancelRetry]);

    // Track loaded product IDs to prevent duplicates when batch sizes don't align
    // (e.g., initial load returns 40 products, View More requests 14 at a time)
    // On POP cache restore, initialize from cached products (which include former
    // skeleton pages) to prevent dedup failures on subsequent View More clicks.
    const keyOf = useCallback(
        (model: TileableModel) => dedupeKey?.(model) ?? model.id,
        [dedupeKey],
    );
    const keyOfRef = useRef(keyOf);
    keyOfRef.current = keyOf;
    const loadedIdsRef = useRef<Set<string | number>>(
        new Set(
            (cachedState?.products ?? initialProducts).map((p) =>
                keyOfRef.current(p.model),
            ),
        ),
    );

    // Ref to track the current category/filters/search/sort for detecting changes
    const categoryRef = useRef(categoryLink);
    const filtersRef = useRef(filters);
    const searchRef = useRef(search);
    const sortRef = useRef(sortBy);

    // =========================================================================
    // Skeleton Lazy-Loading State
    // =========================================================================

    // Store loaded skeleton page products (Map: pageNumber -> products)
    const [skeletonProducts, setSkeletonProducts] = useState<
        Map<number, TileViewModel<TileableModel>[]>
    >(new Map());

    // Track which skeleton pages are currently loading
    const [loadingPages, setLoadingPages] = useState<Set<number>>(new Set());

    // Track the current first loaded page (updates when skeleton pages load)
    // When restored from cache, all products are present → no skeletons needed
    const [currentFirstLoadedPage, setCurrentFirstLoadedPage] = useState(
        isRestoredFromCache ? 1 : firstLoadedPage,
    );

    // Track which fetcher is loading which page (fetcher index -> page number)
    const fetcherPageMapRef = useRef<Map<number, number>>(new Map());

    // Queue for overflow requests when all fetchers are busy
    const pendingQueueRef = useRef<number[]>([]);

    // Track which skeleton fetchers have been processed (to avoid double-processing)
    const processedFetchersRef = useRef<Set<number>>(new Set());
    // Skeleton pages whose read came back as a failed page and have already
    // been put back on the queue once. A second automatic attempt would be
    // hammering a gateway that has just said no; past that the page waits for
    // the reader to bring it back into view.
    const retriedSkeletonPagesRef = useRef<Set<number>>(new Set());

    // Track which skeleton pages have completed loading (for contiguous calculation).
    // currentFirstLoadedPage only decreases when pages form a contiguous block
    // downward from the original firstLoadedPage, preventing layout shifts when
    // parallel skeleton fetchers complete out of order.
    const loadedSkeletonPagesRef = useRef<Set<number>>(new Set());

    // Track if this is the initial mount (to skip reset effects on first render)
    const isInitialMountRef = useRef(true);

    // Track previous initialProducts reference to detect actual navigation changes
    const prevInitialProductsRef = useRef<
        TileViewModel<TileableModel>[] | null
    >(null);

    // Tracks the effective first page of the products array for offset calculation.
    // After POP cache restore, products start from page 1 (includes former skeleton pages),
    // but firstLoadedPage from the loader may be higher (e.g., 5 for ?page=7).
    // Using firstLoadedPage directly would double-count skeleton products in the offset.
    const effectiveFirstPageRef = useRef(
        isRestoredFromCache ? 1 : firstLoadedPage,
    );

    // Use server's hasMore state (updated from action responses). A closed
    // content gate overrides it: the tiles beyond the cut-off are not the
    // visitor's to fetch, and `loadMore` already refuses to run without it.
    const hasMore = gated ? false : serverHasMore;
    // isLoading stays true until BOTH: fetcher is idle AND products have been appended.
    // This eliminates the gap render where the skeleton disappears before new sections appear.
    const isLoading =
        isViewMoreLoading || fetcher.state === 'loading' || retryPending;

    // Reset state when category, filters, search or sort change
    useEffect(() => {
        const categoryChanged = categoryRef.current !== categoryLink;
        const filtersChanged =
            JSON.stringify(filtersRef.current) !== JSON.stringify(filters);
        // A new term is a new result set: the pages already loaded came from
        // the old one, so keeping them would mix two searches in one grid.
        const searchChanged = searchRef.current !== search;
        const sortChanged = sortRef.current !== sortBy;

        if (categoryChanged || filtersChanged || searchChanged || sortChanged) {
            // Clear stale cache entry when navigating to a different category
            if (categoryChanged) {
                viewMoreCache.delete(categoryRef.current);
            }

            // Update refs
            categoryRef.current = categoryLink;
            filtersRef.current = filters;
            searchRef.current = search;
            sortRef.current = sortBy;

            // Reset state
            setProducts(initialProducts);
            setError(null);
            setIsViewMoreLoading(false);
            cancelRetry();
            // Reset hasMore based on new initial state (offset-aware for deep-links)
            setServerHasMore(
                offsetBase +
                    tileCountForPage(firstLoadedPage - 1) +
                    initialProducts.length <
                    totalCount,
            );
            // Mark any in-flight fetch as stale so its response is ignored
            processedFetchCountRef.current = fetchCountRef.current;
            nextOffsetRef.current = null;

            // Reset loaded IDs with new initial products
            loadedIdsRef.current = new Set(
                initialProducts.map((p) => keyOfRef.current(p.model)),
            );

            // Reset skeleton state
            setSkeletonProducts(new Map());
            setLoadingPages(new Set());
            setCurrentFirstLoadedPage(firstLoadedPage);
            effectiveFirstPageRef.current = firstLoadedPage;
            fetcherPageMapRef.current.clear();
            pendingQueueRef.current = [];
            processedFetchersRef.current.clear();
            retriedSkeletonPagesRef.current.clear();
            loadedSkeletonPagesRef.current.clear();
        }
    }, [
        categoryLink,
        filters,
        search,
        sortBy,
        initialProducts,
        totalCount,
        firstLoadedPage,
        cancelRetry,
    ]);

    // Handle fetcher data when it arrives.
    // Uses a fetch counter instead of the previous hasFetchedRef pattern.
    // This is safe under React 18's startTransition because it doesn't depend
    // on observing the 'loading' state — it only checks whether there's an
    // unprocessed fetch (processedFetchCount < fetchCount).
    useEffect(() => {
        if (
            fetcher.state === 'idle' &&
            fetcher.data &&
            processedFetchCountRef.current < fetchCountRef.current
        ) {
            processedFetchCountRef.current = fetchCountRef.current;

            const response = fetcher.data;

            if (response.error) {
                // The page is still there to fetch; only this read failed.
                // Wait out the gateway and try again, a little longer each
                // time, behind the same skeleton — then hand the decision to
                // the reader with the message and the View More control.
                setIsViewMoreLoading(false);
                if (failedAttemptsRef.current < MAX_AUTO_RETRIES) {
                    const delay =
                        RETRY_BASE_DELAY_MS + Math.random() * RETRY_JITTER_MS;
                    failedAttemptsRef.current += 1;
                    setRetryPending(true);
                    retryTimerRef.current = setTimeout(() => {
                        retryTimerRef.current = null;
                        // Cleared here rather than in `loadMore`, which
                        // clears it past its own guard: a retry that arrives
                        // to find the gate closed or a fetch already running
                        // would otherwise leave the skeleton up for good.
                        setRetryPending(false);
                        loadMoreRef.current();
                    }, delay);
                } else {
                    failedAttemptsRef.current = 0;
                    setError(LOAD_MORE_FAILED_MESSAGE);
                }
                return;
            }
            failedAttemptsRef.current = 0;

            if (response.tiles && response.tiles.length > 0) {
                // Cast tiles to avoid JsonifyObject type incompatibility
                const newProducts =
                    response.tiles as unknown as TileViewModel<TileableModel>[];

                // Filter out duplicates (can occur when batch sizes don't align)
                // Log warnings to help surface offset calculation bugs
                const uniqueNewProducts = newProducts.filter((p) => {
                    const key = keyOfRef.current(p.model);
                    if (loadedIdsRef.current.has(key)) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                '[useProductViewMore] Duplicate product detected:',
                                {
                                    id: p.model.id,
                                    totalLoaded: loadedIdsRef.current.size,
                                },
                            );
                        }
                        return false; // Skip duplicate
                    }
                    loadedIdsRef.current.add(key);
                    return true;
                });

                if (uniqueNewProducts.length > 0) {
                    setProducts((prev) => [...prev, ...uniqueNewProducts]);
                }
                setError(null);

                // Update hasMore from server response
                if (response.hasMore !== undefined) {
                    setServerHasMore(response.hasMore);
                }
                if (typeof response.nextOffset === 'number') {
                    nextOffsetRef.current = response.nextOffset;
                }
            } else if (response.tiles && response.tiles.length === 0) {
                // No more products, but not an error
                setError(null);
                // Server returned empty array, no more products to load
                setServerHasMore(false);
            }

            // Clear View More loading AFTER products are set (same React batch).
            // Both setProducts() and setIsViewMoreLoading(false) are in the same
            // synchronous useEffect body, so React batches them into one render —
            // the skeleton disappears in the same frame as new sections appear.
            setIsViewMoreLoading(false);
        }
    }, [fetcher.state, fetcher.data]);

    // Reset products when initialProducts actually changes (not on initial mount)
    // This prevents wiping skeleton loading state during initial render
    useEffect(() => {
        // Skip initial mount - just store the reference
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            prevInitialProductsRef.current = initialProducts;
            return;
        }

        // Only reset if initialProducts reference actually changed (navigation occurred)
        if (prevInitialProductsRef.current !== initialProducts) {
            prevInitialProductsRef.current = initialProducts;

            setProducts(initialProducts);
            setError(null);
            setIsViewMoreLoading(false);
            // Reset hasMore based on new initial state (offset-aware for deep-links)
            setServerHasMore(
                offsetBase +
                    tileCountForPage(firstLoadedPage - 1) +
                    initialProducts.length <
                    totalCount,
            );
            // The server's row count belonged to the set being replaced; left
            // standing it would send the next View More to an offset in the old
            // result set.
            nextOffsetRef.current = null;
            // Reset loaded IDs with new initial products
            loadedIdsRef.current = new Set(
                initialProducts.map((p) => keyOfRef.current(p.model)),
            );

            // Reset skeleton state for new navigation
            setSkeletonProducts(new Map());
            setLoadingPages(new Set());
            setCurrentFirstLoadedPage(firstLoadedPage);
            effectiveFirstPageRef.current = firstLoadedPage;
            fetcherPageMapRef.current.clear();
            pendingQueueRef.current = [];
            processedFetchersRef.current.clear();
            retriedSkeletonPagesRef.current.clear();
            loadedSkeletonPagesRef.current.clear();
            // The set being replaced owned every pending retry, and a retry
            // left standing holds the skeleton up over results that have
            // already arrived.
            cancelRetry();
        }
    }, [initialProducts, totalCount, firstLoadedPage, cancelRetry]);

    // =========================================================================
    // Skeleton Fetcher Pool Response Handler
    // =========================================================================

    // Helper to process a completed fetcher response
    const processSkeletonFetcherResponse = useCallback(
        (fetcherIndex: number, response: LoadMoreResponse) => {
            const pageNumber = fetcherPageMapRef.current.get(fetcherIndex);
            if (pageNumber === undefined) return;

            // Clean up the fetcher mapping
            fetcherPageMapRef.current.delete(fetcherIndex);

            if (response.error) {
                // A failed page arrives as a page with no tiles, which would
                // otherwise leave this one shimmering for as long as the
                // reader stays beside it — the observer only re-fires when the
                // skeleton leaves the viewport and comes back. Returned rather
                // than re-queued here, so the caller can hold it back: the
                // gateway fails in bursts, and a second read fired the instant
                // the first came back is spent inside the same burst.
                const alreadyRetried =
                    retriedSkeletonPagesRef.current.has(pageNumber);
                retriedSkeletonPagesRef.current.add(pageNumber);
                setLoadingPages((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(pageNumber);
                    return newSet;
                });
                return alreadyRetried ? undefined : pageNumber;
            }

            if (response.tiles && response.tiles.length > 0) {
                // Cast tiles to avoid JsonifyObject type incompatibility
                const newProducts =
                    response.tiles as unknown as TileViewModel<TileableModel>[];

                // Filter out duplicates and add to loaded IDs
                // Log warnings to help surface offset calculation bugs
                const uniqueProducts = newProducts.filter((p) => {
                    const key = keyOfRef.current(p.model);
                    if (loadedIdsRef.current.has(key)) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                '[useProductViewMore] Duplicate product in skeleton page:',
                                {
                                    id: p.model.id,
                                    pageNumber,
                                    totalLoaded: loadedIdsRef.current.size,
                                },
                            );
                        }
                        return false;
                    }
                    loadedIdsRef.current.add(key);
                    return true;
                });

                if (uniqueProducts.length > 0) {
                    // Store skeleton products for this page
                    setSkeletonProducts((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(pageNumber, uniqueProducts);
                        return newMap;
                    });

                    // Update currentFirstLoadedPage only when pages form a contiguous
                    // block downward from the current value. This prevents layout shifts
                    // when parallel fetchers complete out of order (e.g., page 1 before 2-3).
                    loadedSkeletonPagesRef.current.add(pageNumber);
                    setCurrentFirstLoadedPage((prev) => {
                        let newFirst = prev;
                        while (
                            newFirst > 1 &&
                            loadedSkeletonPagesRef.current.has(newFirst - 1)
                        ) {
                            newFirst--;
                        }
                        return newFirst;
                    });
                }
            }

            // Remove from loading set
            setLoadingPages((prev) => {
                const newSet = new Set(prev);
                newSet.delete(pageNumber);
                return newSet;
            });
        },
        [],
    );

    // Process pending queue when a fetcher becomes available
    // Uses functional state update to avoid stale closure issues
    const processQueue = useCallback(() => {
        if (pendingQueueRef.current.length === 0) return;

        // Find an idle fetcher that isn't already assigned
        const idleFetcherIndex = skeletonFetchers.findIndex(
            (f, idx) =>
                f.state === 'idle' && !fetcherPageMapRef.current.has(idx),
        );

        if (idleFetcherIndex === -1) return;

        const nextPage = pendingQueueRef.current.shift();
        if (nextPage === undefined) return;

        // Use functional state update to check current state and avoid stale closure
        setLoadingPages((currentLoadingPages) => {
            // Check if page is already loading
            if (currentLoadingPages.has(nextPage)) {
                return currentLoadingPages;
            }

            // Check if page is already loaded (use ref to get current skeleton products)
            // Note: skeletonProducts state check moved to the guard in loadSkeletonPage
            // The fetcher assignment happens synchronously, so this is safe

            // Assign fetcher to this page
            fetcherPageMapRef.current.set(idleFetcherIndex, nextPage);

            // Calculate offset and limit (all pages are 42 products)
            const offset = tileCountForPage(nextPage - 1);
            const limit = pageSize;

            const params = new URLSearchParams({
                entityType,
                categoryLink,
                offset: offset.toString(),
                limit: limit.toString(),
            });

            if (filters && Object.keys(filters).length > 0) {
                params.set('filters', JSON.stringify(filters));
            }

            if (search) {
                params.set('search', search);
            }

            if (sortBy) {
                params.set('sort', sortBy);
            }

            const url = `${actionPath}?${params.toString()}`;
            const targetFetcher = skeletonFetchers[idleFetcherIndex];
            if (targetFetcher) {
                targetFetcher.load(url);
            }

            // Return updated loading pages set
            return new Set([...currentLoadingPages, nextPage]);
        });
    }, [skeletonFetchers, categoryLink, filters, search, sortBy]);
    useEffect(() => {
        processQueueRef.current = processQueue;
    }, [processQueue]);

    // Consolidated effect for all skeleton fetchers
    // This prevents race conditions from having separate effects for each fetcher
    useEffect(() => {
        // Process each fetcher in order
        const fetcherDataArray = [
            { fetcher: skeletonFetcher0, index: 0 },
            { fetcher: skeletonFetcher1, index: 1 },
            { fetcher: skeletonFetcher2, index: 2 },
        ];

        for (const { fetcher, index } of fetcherDataArray) {
            // Handle completed fetcher response
            if (
                fetcher.state === 'idle' &&
                fetcher.data &&
                fetcherPageMapRef.current.has(index) &&
                !processedFetchersRef.current.has(index)
            ) {
                processedFetchersRef.current.add(index);
                // Cast to avoid JsonifyObject type issue
                const retryPage = processSkeletonFetcherResponse(
                    index,
                    fetcher.data as unknown as LoadMoreResponse,
                );
                processQueue();
                if (retryPage !== undefined) {
                    const timer = setTimeout(
                        () => {
                            skeletonRetryTimersRef.current.delete(timer);
                            pendingQueueRef.current.push(retryPage);
                            processQueueRef.current();
                        },
                        // Jittered for the same reason the View More retry
                        // is: three fetchers here, and every reader with a
                        // deep link open schedules theirs in the same instant.
                        RETRY_BASE_DELAY_MS + Math.random() * RETRY_JITTER_MS,
                    );
                    skeletonRetryTimersRef.current.add(timer);
                }
            }

            // Reset processed flag when fetcher starts loading
            if (fetcher.state === 'loading') {
                processedFetchersRef.current.delete(index);
            }
        }
    }, [
        skeletonFetcher0,
        skeletonFetcher1,
        skeletonFetcher2,
        processSkeletonFetcherResponse,
        processQueue,
    ]);

    // =========================================================================
    // Load Skeleton Page Function
    // =========================================================================

    // Implementation that captures current state — identity changes on every
    // state update, which is fine because it's only used via the ref below.
    const loadSkeletonPageImpl = useCallback(
        (pageNumber: number) => {
            // Don't load if:
            // - Already loading this page
            // - Already loaded this page
            // - Page is >= currentFirstLoadedPage (not a skeleton)
            if (
                loadingPages.has(pageNumber) ||
                skeletonProducts.has(pageNumber) ||
                pageNumber >= currentFirstLoadedPage
            ) {
                return;
            }

            // Find an idle fetcher that isn't already assigned to a page
            const idleFetcherIndex = skeletonFetchers.findIndex(
                (f, idx) =>
                    f.state === 'idle' && !fetcherPageMapRef.current.has(idx),
            );

            if (idleFetcherIndex === -1) {
                // All fetchers busy - queue the request if not already queued
                if (!pendingQueueRef.current.includes(pageNumber)) {
                    pendingQueueRef.current.push(pageNumber);
                }
                return;
            }

            // Mark as loading
            setLoadingPages((prev) => new Set([...prev, pageNumber]));
            fetcherPageMapRef.current.set(idleFetcherIndex, pageNumber);

            // Calculate offset and limit for this page (all pages are 42 products)
            const offset = tileCountForPage(pageNumber - 1);
            const limit = pageSize;

            // Build request
            const params = new URLSearchParams({
                entityType,
                categoryLink,
                offset: offset.toString(),
                limit: limit.toString(),
            });

            // Add filters if present
            if (filters && Object.keys(filters).length > 0) {
                params.set('filters', JSON.stringify(filters));
            }

            if (search) {
                params.set('search', search);
            }

            if (sortBy) {
                params.set('sort', sortBy);
            }

            const url = `${actionPath}?${params.toString()}`;
            const targetFetcher = skeletonFetchers[idleFetcherIndex];
            if (targetFetcher) {
                targetFetcher.load(url);
            }
        },
        [
            loadingPages,
            skeletonProducts,
            currentFirstLoadedPage,
            categoryLink,
            filters,
            search,
            skeletonFetchers,
            sortBy,
        ],
    );

    // Stable ref pattern: the ref always points to the latest implementation,
    // while the wrapper callback has a stable identity (empty deps). This prevents
    // React.memo'd DirectorySkeletonSection from getting stale closures — its
    // IntersectionObserver always delegates to the current implementation.
    const loadSkeletonPageRef = useRef(loadSkeletonPageImpl);
    loadSkeletonPageRef.current = loadSkeletonPageImpl;
    // A gated request is served page 1 only, so no skeleton page should exist to
    // scroll into view. Belt and braces: were one ever rendered, it must not
    // fetch tiles the gate is there to withhold.
    const gatedRef = useRef(gated);
    gatedRef.current = gated;

    const loadSkeletonPage = useCallback((pageNumber: number) => {
        if (gatedRef.current) return;
        loadSkeletonPageRef.current(pageNumber);
    }, []);

    // =========================================================================
    // Merge Skeleton Products with Server/View More Products
    // =========================================================================

    // Combine skeleton products + server-loaded products + view more products
    // Products are merged in page order for correct grid rendering.
    // Only includes skeleton pages that form a contiguous block between
    // currentFirstLoadedPage and firstLoadedPage. Pages loaded out of order
    // wait in skeletonProducts until the gap is filled, preventing layout shifts.
    const allProducts = useMemo(() => {
        if (skeletonProducts.size === 0) {
            return products;
        }

        // Only include skeleton pages contiguous with server-loaded pages
        const skeletonPageNumbers = Array.from(skeletonProducts.keys())
            .filter((p) => p >= currentFirstLoadedPage && p < firstLoadedPage)
            .sort((a, b) => a - b);

        if (skeletonPageNumbers.length === 0) {
            return products;
        }

        const skeletonProductsList: TileViewModel<TileableModel>[] = [];
        for (const pageNum of skeletonPageNumbers) {
            const pageProducts = skeletonProducts.get(pageNum);
            if (pageProducts) {
                skeletonProductsList.push(...pageProducts);
            }
        }

        return [...skeletonProductsList, ...products];
    }, [skeletonProducts, products, currentFirstLoadedPage, firstLoadedPage]);

    const loadMore = useCallback(() => {
        // Don't load if a fetch is in flight or there is nothing more. A
        // pending retry is not in flight: this is how it fires.
        if (isViewMoreLoading || fetcher.state === 'loading' || !hasMore) {
            return;
        }

        // A manual retry supersedes the automatic one that was waiting.
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        setRetryPending(false);

        // Clear any previous error
        setError(null);

        // Calculate the server's starting offset (where products begin in the catalog).
        // Uses effectiveFirstPageRef instead of firstLoadedPage because after POP cache
        // restore, products include former skeleton pages (start from page 1), but
        // firstLoadedPage from the loader still reflects the deep-link page.
        // `offsetBase` shifts this when the server entered the result set part
        // way in, so the next page continues from there rather than repeating
        // products already on screen.
        const serverStartOffset =
            offsetBase +
            (effectiveFirstPageRef.current > 1
                ? tileCountForPage(effectiveFirstPageRef.current - 1)
                : 0);

        // The offset for View More is where products started + products loaded
        // so far, unless the server reported how far its own read reached.
        const offset =
            nextOffsetRef.current ?? serverStartOffset + products.length;

        // Each View More loads 3 sections (42 products)
        // The last fetch may get fewer if not enough products remain
        const remaining = totalCount - offset;
        const limit = Math.min(pageSize, remaining);

        // Build the URL with query parameters
        const params = new URLSearchParams({
            entityType,
            categoryLink,
            offset: offset.toString(),
            limit: limit.toString(),
        });

        // Add filters if present
        if (filters && Object.keys(filters).length > 0) {
            params.set('filters', JSON.stringify(filters));
        }

        // Repeat the keyword term, as the skeleton loaders above do. Without
        // it a later page comes from the unfiltered set and lands in the same
        // grid as the first, mixing two result sets under one heading.
        if (search) {
            params.set('search', search);
        }

        if (sortBy) {
            params.set('sort', sortBy);
        }

        const url = `${actionPath}?${params.toString()}`;

        // Increment fetch counter so the response effect knows there's a pending fetch
        fetchCountRef.current++;

        // Mark View More as loading BEFORE fetcher.load() so the skeleton stays visible
        // until products are actually appended (see setIsViewMoreLoading(false) in data effect)
        setIsViewMoreLoading(true);

        // Use fetcher.load for GET requests
        fetcher.load(url);

        // Update URL with page parameter (using replaceState to avoid history entries)
        // Calculate the page number after loading the new products. Excludes
        // `offsetBase` so the page number counts what the user has seen rather
        // than the absolute position in the result set.
        // Skipped when the grid owns the param instead (useViewportPageSync):
        // under infinite scroll a request-time rewrite runs ahead of the
        // viewport and can skip page numbers when loads fire back-to-back.
        if (updateUrlOnLoad) {
            const newProductCount = offset - offsetBase + limit;
            const newPage = pageFromTileCount(newProductCount);

            // SSR guard + idempotent check: only update if in browser and page changed
            if (typeof window !== 'undefined' && newPage > 1) {
                const currentUrl = new URL(window.location.href);
                const currentPageParam = currentUrl.searchParams.get('page');

                // Only update if page parameter actually changed
                if (currentPageParam !== newPage.toString()) {
                    currentUrl.searchParams.set('page', newPage.toString());
                    window.history.replaceState({}, '', currentUrl.toString());
                }
            }

            // Track page change for analytics
            tracker.log('DirectoryPageChange', {
                url: new URL(window.location.href),
                targetTracker: 'archiproTracker',
                data: {
                    page: newPage,
                    category: categoryLink,
                },
            });
        }
    }, [
        isViewMoreLoading,
        hasMore,
        categoryLink,
        products.length,
        totalCount,
        filters,
        search,
        fetcher,
        tracker,
        sortBy,
        offsetBase,
        updateUrlOnLoad,
    ]);
    // Assigned on commit rather than during render: the only reader is the
    // retry timer, a second or more later, and a render React throws away
    // must not leave the ref on that render's closure.
    useEffect(() => {
        loadMoreRef.current = loadMore;
    }, [loadMore]);

    // Persist product state to cache for POP navigation restoration.
    // Stores allProducts (skeleton + server + View More) instead of just products,
    // so deep-link POP restores the full product set including former skeleton pages.
    useEffect(() => {
        viewMoreCache.set(categoryLink, {
            products: allProducts,
            serverHasMore,
        });
    }, [categoryLink, allProducts, serverHasMore]);

    // The `?page=N` a Load More / View More click will land on — mirrors the
    // offset maths in loadMore so the crawlable anchor points at the real next
    // page. Recomputed as products load, but only the pre-interaction value
    // (page 1 → 2) is seen by crawlers and the SSR HTML.
    const nextPageServerStartOffset =
        effectiveFirstPageRef.current > 1
            ? tileCountForPage(effectiveFirstPageRef.current - 1)
            : 0;
    const nextPageOffset = nextPageServerStartOffset + products.length;
    const nextPageLimit = Math.max(
        0,
        Math.min(pageSize, totalCount - nextPageOffset),
    );
    const nextPage = pageFromTileCount(nextPageOffset + nextPageLimit);

    return {
        // Return merged products (skeleton + server + View More) for grid rendering
        products: allProducts,
        hasMore,
        isLoading,
        error,
        loadMore,
        // Skeleton lazy-loading
        loadSkeletonPage,
        loadingPages,
        currentFirstLoadedPage,
        isRestoredFromCache,
        nextPage,
    };
}

/**
 * Products-specific alias of {@link useDirectoryViewMore} (entityType defaults to
 * `products`, pageSize to 42). Kept so the products grid reads naturally.
 */
export const useProductViewMore = useDirectoryViewMore;
